import React, { useState, useEffect } from "react";
import "./PatientPortal.css";
import { baseurImage, baseurl } from "../../Baseurl";
import {
    FaCalendarCheck,
    FaHeartbeat,
    FaFileAlt,
    FaFlask,
    FaHistory,
    FaTooth,
    FaNotesMedical,
    FaFileInvoiceDollar,
    FaShieldAlt,
    FaArrowLeft
} from "react-icons/fa";
import axiosInstance from "../../api/axiosInstance";
import { useDispatch, useSelector } from "react-redux";
import { fetchBusinessSettings } from "../../store/businessSlice";
import { useNavigate } from "react-router-dom";

// Import Sub-Components
import PortalAppointments from "./PortalAppointments";
import PortalVitalSigns from "./PortalVitalSigns";
import PortalForms from "./PortalForms";
import PortalLabs from "./PortalLabs";
import PortalMedicalHistory from "./PortalMedicalHistory";
import PortalDental from "./PortalDental";
import PortalServices from "./PortalServices";
import PortalBilling from "./PortalBilling";
import PortalInsurance from "./PortalInsurance";
import PortalPrescriptions from "./PortalPrescriptions";

export default function PatientPortal() {
    const [activeModule, setActiveModule] = useState(null);
    const [patientData, setPatientData] = useState(null);
    const [loading, setLoading] = useState(true);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const businessSettings = useSelector((state) => state.business.businessSettings);

    // MOCK or FETCH Patient ID
    const getPatientId = () => {
        // 1. Check URL Query Param (for testing: /patient?id=101)
        const searchParams = new URLSearchParams(window.location.search);
        const idFromUrl = searchParams.get("id");
        if (idFromUrl) return idFromUrl;

        // 2. Check Local Storage (Logged in User)
        const userMedical = localStorage.getItem("userMedical");
        if (userMedical) {
            const parsed = JSON.parse(userMedical);
            // If a patient is logged in, use their ID.
            if (parsed?.data?.role === "patient" || parsed?.data?.userType === "patient") {
                return parsed?.data?.id;
            }
        }

        // 3. Fallback for testing if nothing else found
        return null;
    };

    const patientId = getPatientId();

    useEffect(() => {
        const fetchPatientData = async () => {
            try {
                const response = await axiosInstance.get(`getPatientById/${patientId}`);
                if (response.data?.success) {
                    setPatientData(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching patient data:", error);
            } finally {
                setLoading(false);
            }
        };

        if (patientId) {
            // Pre-fill from localStorage if available to show data immediately
            const userMedical = localStorage.getItem("userMedical");
            if (userMedical) {
                const parsed = JSON.parse(userMedical);
                if (parsed?.data && (parsed.data.id == patientId)) {
                    // Only set if we haven't fetched yet (or as initial placeholder)
                    setPatientData(prev => prev || parsed.data);
                }
            }

            fetchPatientData();
            // Try to fetch business settings using the patient ID if not already available
            // Note: Use a default ID or logic if patient ID is not appropriate for business settings
            if (!businessSettings) {
                dispatch(fetchBusinessSettings(patientId));
            }
        } else {
            setLoading(false);
        }
    }, [patientId, dispatch, businessSettings]);

    const handleLogout = () => {
        localStorage.clear();
        navigate("/patient/login");
    };

    const modules = [
        { id: "appointments", title: "Appointments", icon: <FaCalendarCheck />, color: "#3b82f6" },
        // { id: "vitals", title: "Vital Signs", icon: <FaHeartbeat />, color: "#ef4444" },
        { id: "forms", title: "Forms", icon: <FaFileAlt />, color: "#f59e0b" },
        { id: "labs", title: "Lab Requests", icon: <FaFlask />, color: "#8b5cf6" },
        { id: "prescriptions", title: "Prescriptions", icon: <FaHistory />, color: "#10b981" },
        // { id: "medical_history", title: "Medical History", icon: <FaHistory />, color: "#10b981" },
        // { id: "dental", title: "Dental", icon: <FaTooth />, color: "#06b6d4" },
        // { id: "services", title: "Services", icon: <FaNotesMedical />, color: "#6366f1" },
        { id: "billing", title: "Billing & Receipts", icon: <FaFileInvoiceDollar />, color: "#22c55e" },
        // { id: "insurance", title: "Insurance", icon: <FaShieldAlt />, color: "#ec4899" },
    ];

    const renderContent = () => {
        switch (activeModule) {
            case "appointments": return <PortalAppointments patientId={patientId} />;
            // case "vitals": return <PortalVitalSigns patientId={patientId} />;
            case "forms": return <PortalForms patientId={patientId} />;
            case "labs": return <PortalLabs patientId={patientId} />;

            case "prescriptions": return <PortalPrescriptions patientId={patientId} /> // "prscriptions" -> "prescriptions"
            // case "medical_history": return <PortalMedicalHistory patientId={patientId} />;
            // case "dental": return <PortalDental patientId={patientId} />;
            // case "services": return <PortalServices patientId={patientId} />;
            case "billing": return <PortalBilling patientId={patientId} />;
            // case "insurance": return <PortalInsurance patientId={patientId} />;
            default: return <div className="p-4">Module Under Construction</div>;
        }
    };

    if (!loading && !patientData) {
        return (
            <div className="portal-container flex items-center justify-center">
                <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-700">Patient Data Not Found</h3>
                    <p className="text-gray-500 mt-2">Please ensure you are logged in as a patient.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="mainPatient mb-4">
            <div className="container-fluid px-0 overflow-hidden">
                <div class="patientHeader">
                    <div className="d-flex justify-content-between align-items-center h-100">
                        <div className="welcomBack h-100 align-items-center">
                            <div className="h-100">
                                <h2 className="mb-0">{businessSettings?.official_name || "Serenity Medical Center W.L.L"}</h2>

                            </div>
                            <div>
                                <h2 className="mb-0">Welcome Back, {patientData?.firstName} {patientData?.lastName}</h2>
                            </div>
                        </div>
                        <div className="pe40 d-none d-md-block">
                            <button className="cancelBtn" onClick={handleLogout}>Logout</button>
                        </div>
                    </div>
                </div>

                <div className="px-4">

                    {/* Header / Profile Section */}
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="my-md-5 my-sm-4 my-4">
                                <div className="table-card card p-0 mt-sm-0 mt-2">
                                    <div className="tableHeader d-none d-md-block">
                                        <h5 className="d-none d-md-block">{patientData?.firstName || "Welcome"} {patientData?.lastName || ""}</h5>
                                    </div>
                                    <div className="portal-header-card p20">
                                        <div>

                                            <img
                                                src={patientData?.profileImage ? `${baseurImage}${patientData.profileImage}` : "https://via.placeholder.com/150"}
                                                alt="Profile"
                                                className="portal-profile-image"
                                                onError={(e) => e.target.src = "https://via.placeholder.com/150"}
                                            />
                                            <h5 className="d-block d-md-none mt-2 mb-0">{patientData?.firstName || "Welcome"} {patientData?.lastName || ""}</h5>
                                        </div>
                                        <div className="portal-profile-info">

                                            <div className="portal-profile-details">
                                                <span><strong>ID:</strong> {patientData?.fileNumber || patientData?.id || "N/A"}</span>
                                                <span><strong>DOB:</strong> {patientData?.dateOfBirth ? new Date(patientData.dateOfBirth).toLocaleDateString() : "N/A"}</span>
                                                <span><strong>Age:</strong> {patientData?.age || "N/A"}</span>
                                                <span><strong>Gender:</strong> {patientData?.gender || "N/A"}</span>
                                                <span><strong>Mobile:</strong> {patientData?.mobileNumber || "N/A"}</span>
                                                <span><strong>Civil ID:</strong> {patientData?.civilIdNumber || patientData?.civilId || "N/A"}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                    {/* Main Content Area */}

                    {activeModule ? (
                        <div className="table-card card">
                            <div>

                                <div className="tableHeader">
                                    <div className="d-flex">
                                        <button className="backArrow" onClick={() => setActiveModule(null)}>
                                            <i class="fa fa-arrow-left"></i>
                                            Back to Dashboard
                                        </button>

                                    </div>
                                </div>
                                <div className="portal-content-wrapper p20">
                                    {renderContent()}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="portal-grid-container fade-in">
                            {modules.map((mod) => (
                                <div
                                    key={mod.id}
                                    className="portal-module-card"
                                    onClick={() => setActiveModule(mod.id)}

                                >
                                    <div className="portal-card-icon" style={{ color: mod.color, backgroundColor: `${mod.color}33` }}>
                                        {mod.icon}
                                    </div>
                                    <h3 className="portal-card-title">{mod.title}</h3>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="row">
                        <div className="col-lg-12 mt-3">
                            <div className="d-block d-md-none text-center">
                                <button className="cancelBtn" onClick={handleLogout}>Logout</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    );
} 
